/**
 * Selection commands shared by the panels, the toolbar and the keyboard
 * shortcuts: one function per user action, each one undo step.
 */

import { duplicateShapes, groupShapes, mirrorShapes, removeParam, removeShapes, translateShapes, ungroup } from './specOps';
import type { EditorStore } from './EditorStore';

/** Shapes the selection covers: the selected shapes, or a selected group's shapes. */
export function selectedShapeIds(store: EditorStore): readonly string[] {
  const { spec, selection } = store.getState();
  if (selection.kind === 'shapes') return selection.ids;
  if (selection.kind === 'group') return spec.groups.find((g) => g.id === selection.id)?.shapeIds ?? [];
  return [];
}

export function groupSelection(store: EditorStore, name: string): void {
  const ids = selectedShapeIds(store);
  if (ids.length === 0) return;
  const { spec, groupId } = groupShapes(store.getState().spec, ids, name);
  store.apply(() => spec, { kind: 'group', id: groupId });
}

export function ungroupSelection(store: EditorStore): void {
  const { selection } = store.getState();
  if (selection.kind !== 'group') return;
  const ids = selectedShapeIds(store);
  store.apply((s) => ungroup(s, selection.id), { kind: 'shapes', ids });
}

/** Delete: shapes, a group with its shapes, or a param. */
export function deleteSelection(store: EditorStore): void {
  const { selection } = store.getState();
  if (selection.kind === 'param') {
    store.apply((s) => removeParam(s, selection.key), { kind: 'none' });
    return;
  }
  const ids = selectedShapeIds(store);
  if (ids.length > 0) store.apply((s) => removeShapes(s, ids), { kind: 'none' });
}

/** Duplicate beside the original (offset 10 cm) and select the copies. */
export function duplicateSelection(store: EditorStore): void {
  const ids = selectedShapeIds(store);
  if (ids.length === 0) return;
  const result = duplicateShapes(store.getState().spec, ids, 0.1, -0.1);
  store.apply(() => result.spec, { kind: 'shapes', ids: result.ids });
}

export function mirrorSelection(store: EditorStore, axis: 'u' | 'n'): void {
  const ids = selectedShapeIds(store);
  if (ids.length > 0) store.apply((s) => mirrorShapes(s, ids, axis));
}

export function nudgeSelection(store: EditorStore, du: number, dn: number): void {
  const ids = selectedShapeIds(store);
  if (ids.length > 0) store.apply((s) => translateShapes(s, ids, du, dn));
}
